import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Dialog, IconButton, InputBase, Menu, MenuItem,
    TextField, Typography, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { downloadProjectBudgetExport, exportProjectBudget, getProjectBudget, getProjectBudgetExports, saveProjectBudget } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { saveBase64File } from '../projectSchedule.utils';
import BudgetLinesTable from './BudgetLinesTable';
import { GROUP_KINDS, STARTER_GROUPS, emptyRow, inr, sheetTotals } from './budget.utils';

const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };
const card = { border: '1px solid #e5e9ee', borderRadius: 3, p: 2, bgcolor: '#fff' };

// Numbers come back from the server as numbers; the inputs edit them as text
const toDraft = (lines) => ({
    revenue: (lines?.revenue || []).map(r => ({ ...r })),
    groups: (lines?.groups || []).map(g => ({ ...g, rows: g.rows.map(r => ({ ...r })) })),
});

function Delta({ now, before }) {
    if (before === undefined || before === null) return null;
    const d = now - before;
    if (Math.abs(d) < 0.5) return <Typography component="span" sx={{ fontSize: '11px', color: '#94a3b8' }}> · no change</Typography>;
    return <Typography component="span" sx={{ fontSize: '11px', color: d > 0 ? '#16a34a' : '#dc2626' }}> · {d > 0 ? '+' : '−'}{inr(Math.abs(d))} vs previous</Typography>;
}

// One budget sheet version: revenue lines, expense groups, live summary with the previous version, PDF downloads
export default function BudgetSheetDialog({ projectId, budgetId, facts, onClose, onSaved }) {
    const dispatch = useDispatch();
    const fullScreen = useMediaQuery('(max-width:900px)');
    const [sheet, setSheet] = useState(null); // server copy
    const [name, setName] = useState('');
    const [lines, setLines] = useState({ revenue: [], groups: [] });
    const [exports, setExports] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(null); // 'save' | 'pdf' | export id
    const [message, setMessage] = useState(null);
    const [addAnchor, setAddAnchor] = useState(null);

    const loadExports = useCallback(() => {
        dispatch(getProjectBudgetExports(projectId, budgetId)).then(res => setExports(res?.data?.success ? res.data.data || [] : []));
    }, [dispatch, projectId, budgetId]);

    const load = useCallback(() => {
        setMessage(null);
        dispatch(getProjectBudget(projectId, budgetId)).then((res) => {
            if (res?.data?.success) {
                const b = res.data.data;
                setSheet(b); setName(b.name); setLines(toDraft(b.lines)); setDirty(false);
            } else setMessage({ severity: 'error', text: res?.data?.message || 'Could not load the budget sheet' });
        });
        loadExports();
    }, [dispatch, projectId, budgetId, loadExports]);

    useEffect(() => { load(); }, [load]);

    const totals = useMemo(() => sheetTotals(lines), [lines]);
    const prev = sheet?.previous?.totals;
    const change = (fn) => { setLines(fn); setDirty(true); };
    const setGroup = (i, patch) => change(l => ({ ...l, groups: l.groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)) }));
    const addGroup = (title, kind) => { change(l => ({ ...l, groups: [...l.groups, { title, kind, rows: [emptyRow(kind)] }] })); setAddAnchor(null); };

    const save = async () => {
        setBusy('save'); setMessage(null);
        const res = await dispatch(saveProjectBudget(projectId, budgetId, { name, lines, updated_at: sheet.updated_at }));
        setBusy(null);
        if (res?.data?.success) {
            setSheet(s => ({ ...res.data.data, previous: s.previous }));
            setLines(toDraft(res.data.data.lines));
            setDirty(false);
            setMessage({ severity: 'success', text: 'Budget saved' });
            onSaved?.();
            return true;
        }
        setMessage({ severity: res?.data?.conflict ? 'warning' : 'error', text: res?.data?.message || 'Could not save', conflict: !!res?.data?.conflict });
        return false;
    };

    const downloadPdf = async () => {
        if (dirty && !(await save())) return;
        setBusy('pdf');
        const res = await dispatch(exportProjectBudget(projectId, budgetId));
        setBusy(null);
        if (res?.data?.success) { saveBase64File(res.data.data.base64, res.data.data.file_name); loadExports(); }
        else setMessage({ severity: 'error', text: res?.data?.message || 'Could not make the PDF' });
    };

    const redownload = async (exp) => {
        setBusy(exp.id);
        const res = await dispatch(downloadProjectBudgetExport(projectId, exp.id));
        setBusy(null);
        if (res?.data?.success) saveBase64File(res.data.data.base64, res.data.data.file_name);
        else setMessage({ severity: 'error', text: res?.data?.message || 'Could not download' });
    };

    const close = () => {
        if (dirty && !window.confirm('You have changes that are not saved. Close without saving?')) return;
        onClose();
    };

    const maxGroup = Math.max(1, ...totals.by_group.map(g => g.amount));

    return (
        <Dialog open onClose={close} fullWidth maxWidth="lg" fullScreen={fullScreen} PaperProps={{ sx: { height: fullScreen ? '100%' : '92vh', borderRadius: fullScreen ? 0 : 4, bgcolor: '#f6f8f9' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.25, py: 1.25, bgcolor: '#fff', borderBottom: '1px solid #e5e9ee', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontWeight: 700 }}>💰 Budget sheet{sheet ? ` · Version ${sheet.version}` : ''}</Typography>
                    <Typography noWrap sx={{ fontSize: '12.5px', color: '#6b7785' }}>
                        {[facts.facility_name, facts.project_type, facts.contract_years ? `${facts.contract_years}-year contract` : null].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={close} aria-label="Close"><CloseIcon /></IconButton>
            </Box>

            {!sheet ? (
                <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>{message ? <Alert severity="error">{message.text}</Alert> : <CircularProgress sx={{ color: THEME_COLOR }} />}</Box>
            ) : (
                <Box sx={{ flex: 1, overflow: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0,1fr) 300px' }, gap: 2, p: 2, alignItems: 'start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                        <Box sx={card}>
                            <TextField size="small" label="Sheet name" value={name} onChange={e => { setName(e.target.value); setDirty(true); }} sx={{ ...fieldSx, mb: 1.5, width: { xs: '100%', sm: 320 } }} inputProps={{ maxLength: 255 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1 }}>Revenue</Typography>
                            <BudgetLinesTable rows={lines.revenue} kind="qr" total={totals.revenue} nameLabel="Revenue item"
                                onChange={rows => change(l => ({ ...l, revenue: rows }))} />
                        </Box>
                        <Box sx={card}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '14px', flex: 1 }}>Expenses</Typography>
                                <Button size="small" variant="outlined" onClick={e => setAddAnchor(e.currentTarget)} sx={buttonStyles.outlined}>＋ Expense group</Button>
                                <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)} disableScrollLock>
                                    {STARTER_GROUPS.map(g => <MenuItem key={g.title} onClick={() => addGroup(g.title, g.kind)}>{g.title} <Typography component="span" sx={{ ml: 1, fontSize: '12px', color: '#6b7785' }}>{GROUP_KINDS[g.kind].label}</Typography></MenuItem>)}
                                    <MenuItem onClick={() => addGroup('Other expenses', 'qr')}>Other (qty × unit cost)</MenuItem>
                                    <MenuItem onClick={() => addGroup('Other people costs', 'dpr')}>Other (days × persons × rate)</MenuItem>
                                </Menu>
                            </Box>
                            {!lines.groups.length && <Typography sx={{ color: '#6b7785', fontSize: '13px', py: 1 }}>No expenses yet. Add a group such as materials or manpower.</Typography>}
                            {lines.groups.map((g, i) => (
                                <Box key={i} sx={{ mt: i ? 2 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                        <InputBase value={g.title} onChange={e => setGroup(i, { title: e.target.value })} inputProps={{ maxLength: 100 }}
                                            sx={{ fontWeight: 600, fontSize: '13.5px', flex: 1, '& input': { p: '2px 4px', borderRadius: '4px' }, '& input:focus': { bgcolor: '#f4f6f8' } }} />
                                        <Typography sx={{ fontSize: '11.5px', color: '#6b7785' }}>{GROUP_KINDS[g.kind].label}</Typography>
                                        <IconButton size="small" aria-label="Remove group" onClick={() => { if (!g.rows.some(r => r.name) || window.confirm(`Remove “${g.title}” and its rows?`)) change(l => ({ ...l, groups: l.groups.filter((_, idx) => idx !== i) })); }}>
                                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                    <BudgetLinesTable rows={g.rows} kind={g.kind} total={totals.by_group[i]?.amount || 0} onChange={rows => setGroup(i, { rows })} />
                                </Box>
                            ))}
                        </Box>
                        {exports.length > 0 && (
                            <Accordion disableGutters sx={{ border: '1px solid #e5e9ee', boxShadow: 'none', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography sx={{ fontSize: '13.5px', fontWeight: 500 }}>PDF downloads · {exports.length}</Typography></AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    {exports.map(e => (
                                        <Box key={e.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: '1px solid #eef1f4', fontSize: '12.5px' }}>
                                            <Box sx={{ flex: 1 }}>{dayjs(e.created_at).format('D MMM YYYY, h:mm a')} · {e.created_by_name} · profit {inr(e.profit)}{e.margin !== null ? ` (${e.margin}%)` : ''}</Box>
                                            <Button size="small" onClick={() => redownload(e)} disabled={busy === e.id} sx={{ color: THEME_COLOR, textTransform: 'none' }}>{busy === e.id ? <CircularProgress size={14} /> : '⬇ PDF'}</Button>
                                        </Box>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </Box>

                    <Box sx={{ ...card, position: { md: 'sticky' }, top: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px', mb: 1 }}>Summary</Typography>
                        {[['Revenue', totals.revenue, prev?.revenue], ['Expenses', totals.expenses, prev?.expenses]].map(([l, v, p]) => (
                            <Box key={l} sx={{ mb: 0.75 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}><span>{l}</span><b>{inr(v)}</b></Box>
                                <Delta now={v} before={p} />
                            </Box>
                        ))}
                        <Box sx={{ borderTop: '1px solid #e5e9ee', pt: 1, mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 700, color: totals.profit < 0 ? '#dc2626' : '#196a3e' }}><span>Profit</span><span>{inr(totals.profit)}</span></Box>
                            <Delta now={totals.profit} before={prev?.profit} />
                            <Typography sx={{ fontSize: '13px', color: '#6b7785', mt: 0.5 }}>
                                Margin <b style={{ color: '#1f2933' }}>{totals.margin === null ? '—' : `${totals.margin}%`}</b>
                                {prev && prev.margin !== null && totals.margin !== null && ` (was ${prev.margin}%)`}
                            </Typography>
                        </Box>
                        {totals.by_group.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                                <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', mb: 0.75 }}>Cost breakdown</Typography>
                                {totals.by_group.map((g, i) => (
                                    <Box key={i} sx={{ mb: 0.75 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}><span>{g.title}</span><span>{inr(g.amount)}</span></Box>
                                        <Box sx={{ height: 5, bgcolor: '#eef1f4', borderRadius: 3 }}><Box sx={{ height: 5, width: `${(g.amount / maxGroup) * 100}%`, bgcolor: THEME_COLOR, borderRadius: 3 }} /></Box>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {sheet.previous && <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', mt: 1.5 }}>Compared with Version {sheet.previous.version}</Typography>}
                    </Box>
                </Box>
            )}

            {sheet && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 1.25, bgcolor: '#fff', borderTop: '1px solid #e5e9ee', flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        {message
                            ? <Alert severity={message.severity} sx={{ py: 0 }} onClose={() => setMessage(null)} action={message.conflict ? <Button color="inherit" size="small" onClick={load}>Reload</Button> : null}>{message.text}</Alert>
                            : <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{dirty ? 'Unsaved changes' : `Saved ${dayjs(sheet.updated_at).format('D MMM, h:mm a')}${sheet.updated_by_name ? ` by ${sheet.updated_by_name}` : ''}`}</Typography>}
                    </Box>
                    <Button variant="outlined" onClick={downloadPdf} disabled={!!busy} sx={buttonStyles.outlined}>{busy === 'pdf' ? <CircularProgress size={18} /> : '⬇ PDF'}</Button>
                    <Button variant="outlined" onClick={close} disabled={busy === 'save'} sx={buttonStyles.outlined}>Done</Button>
                    <Button variant="contained" onClick={save} disabled={!dirty || !!busy} sx={buttonStyles.contained}>{busy === 'save' ? <CircularProgress size={18} color="inherit" /> : 'Update'}</Button>
                </Box>
            )}
        </Dialog>
    );
}
